import pino from 'pino';
import { isDatadogInitialized, sendToDatadog } from './DatadogTransport';

// Edge Runtime compatible logger configuration
const pinoLogger =
  process.env.NODE_ENV !== 'production'
    ? pino({
        level: 'trace',
        // Custom formatting for development
        formatters: {
          level: (label) => {
            return { level: label };
          },
        },
        timestamp: () => `,"time":"${new Date().toLocaleTimeString()}"`,
        base: null, // This removes pid, hostname, etc.
      })
    : pino({
        level: 'warn',
        base: null, // This removes pid, hostname, etc.
      });

/**
 * Send log to Datadog if initialized and on client side
 * In production, only send error and warn levels for performance
 */
function logToDatadog(level: string, msg: string, obj?: unknown): void {
  if (typeof window !== 'undefined' && isDatadogInitialized()) {
    // In production, only send error and warn logs to Datadog
    const isProduction = process.env.NODE_ENV === 'production';
    const shouldSend = !isProduction || level === 'error' || level === 'warn';

    if (shouldSend) {
      sendToDatadog(level, msg, obj);
    }
  }
}

function serializeError(error: unknown) {
  return {
    ...(typeof error === 'object' && error !== null ? error : {}),
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    name: error instanceof Error ? error.name : undefined,
  };
}

/**
 * Logger that handles unknown error types in warn and error methods
 */
const logger = {
  info: (msg: string, obj?: unknown) => {
    const result =
      obj !== undefined
        ? // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
          (pinoLogger as any).info(msg, obj)
        : pinoLogger.info(msg);
    logToDatadog('info', msg, obj);
    return result;
  },
  warn: (
    msg: string,
    obj?: { error?: unknown; [key: string]: unknown } | unknown
  ) => {
    let result: unknown;
    if (obj && typeof obj === 'object' && 'error' in obj && obj.error) {
      const errorObj = {
        ...obj,
        error: serializeError(obj.error),
      };

      // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
      result = (pinoLogger as any).warn(msg, errorObj);
      logToDatadog('warn', msg, errorObj);
    } else {
      result =
        obj !== undefined
          ? // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
            (pinoLogger as any).warn(msg, obj)
          : pinoLogger.warn(msg);
      logToDatadog('warn', msg, obj);
    }
    return result;
  },
  error: (
    msg: string,
    obj?: { error?: unknown; [key: string]: unknown } | unknown
  ) => {
    let result: unknown;
    if (obj && typeof obj === 'object' && 'error' in obj && obj.error) {
      const errorObj = {
        ...obj,
        error: serializeError(obj.error),
      };

      // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
      result = (pinoLogger as any).error(msg, errorObj);
      logToDatadog('error', msg, errorObj);
    } else {
      result =
        obj !== undefined
          ? // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
            (pinoLogger as any).error(msg, obj)
          : pinoLogger.error(msg);
      logToDatadog('error', msg, obj);
    }
    return result;
  },
  debug: (msg: string, obj?: unknown) => {
    const result =
      obj !== undefined
        ? // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
          (pinoLogger as any).debug(msg, obj)
        : pinoLogger.debug(msg);
    logToDatadog('debug', msg, obj);
    return result;
  },
  trace: (msg: string, obj?: unknown) => {
    const result =
      obj !== undefined
        ? // biome-ignore lint/suspicious/noExplicitAny: Pino requires any for flexible logging
          (pinoLogger as any).trace(msg, obj)
        : pinoLogger.trace(msg);
    logToDatadog('trace', msg, obj);
    return result;
  },
};

export default logger;
